import {
  Check,
  Mail,
  PackageCheck,
  ReceiptText,
  X,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import OathboundFrame from "@/components/OathboundFrame/OathboundFrame";
import {
  clearPurchaseOutcome,
  isPurchaseOutcome,
  readPurchaseOutcome,
} from "@/components/PurchasePage/purchaseCheckout";
import "./PurchaseReceiptPage.css";

function formatPaise(amountPaise, currency = "INR") {
  const hasPaise = amountPaise % 100 !== 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  }).format(amountPaise / 100);
}

function getOutcome(locationState, previewOutcome) {
  if (previewOutcome) return previewOutcome;
  if (isPurchaseOutcome(locationState)) return locationState;
  return readPurchaseOutcome();
}

function ReturnHomeLink() {
  return (
    <Link
      className="purchase-receipt-home"
      to="/#purchase"
      onClick={() => clearPurchaseOutcome()}
    >
      Return home
    </Link>
  );
}

function ReceiptDetails({ receipt }) {
  return (
    <dl className="purchase-receipt-details" aria-label="Order summary">
      <div>
        <dt>Order number</dt>
        <dd>#{receipt.trackingNumber}</dd>
      </div>
      <div>
        <dt>Edition</dt>
        <dd>Premium hardcover</dd>
      </div>
      <div>
        <dt>Quantity</dt>
        <dd>{receipt.quantity}</dd>
      </div>
      <div>
        <dt>Total paid</dt>
        <dd>{formatPaise(receipt.totalAmountPaise, receipt.currency)}</dd>
      </div>
    </dl>
  );
}

function SuccessReceipt({ receipt }) {
  return (
    <>
      <div className="purchase-receipt-status is-success" aria-hidden="true">
        <Check />
      </div>
      <p className="purchase-receipt-eyebrow">Payment confirmed</p>
      <h1 id="purchase-receipt-title">Your order has been placed</h1>
      <p className="purchase-receipt-intro">
        Thank you for purchasing <em>The Oath of the Unhuan</em>. Your copy is
        now being prepared, and order <strong>#{receipt.trackingNumber}</strong>{" "}
        is the number to keep for tracking and support.
      </p>

      <ReceiptDetails receipt={receipt} />

      <section className="purchase-receipt-note" aria-labelledby="delivery-title">
        <PackageCheck aria-hidden="true" />
        <div>
          <h2 id="delivery-title">Packed with care</h2>
          <p>
            Your book will be delivered in a protective box. Delhi and NCR
            orders usually arrive in 1–2 days; elsewhere in India, delivery is
            usually 3–4 days, with a maximum expected window of 7 days.
          </p>
        </div>
      </section>

      <section className="purchase-receipt-note" aria-labelledby="review-title">
        <Mail aria-hidden="true" />
        <div>
          <h2 id="review-title">A note for your inbox</h2>
          <p>
            Keep an eye on the email address used at checkout for your receipt
            and personal review link. Please leave your review after you have
            read some or all of the book—your thoughts are deeply valuable.
          </p>
        </div>
      </section>

      <div className="purchase-receipt-signoff">
        <span>With gratitude,</span>
        <strong>Mahesh Thakur</strong>
        <small>Author</small>
      </div>

      <ReturnHomeLink />

      <p className="purchase-receipt-reference">
        Payment reference: {receipt.paymentReference}
      </p>
    </>
  );
}

function FailureReceipt({ failure, unavailable = false }) {
  const reference = failure?.paymentReference || failure?.providerOrderId;
  const verificationUncertain = failure?.kind === "verification";

  return (
    <>
      <div className="purchase-receipt-status is-failure" aria-hidden="true">
        <X />
      </div>
      <p className="purchase-receipt-eyebrow">
        {unavailable ? "Receipt unavailable" : "Payment incomplete"}
      </p>
      <h1 id="purchase-receipt-title">
        {unavailable ? "We couldn’t load this receipt" : "The payment failed"}
      </h1>
      <p className="purchase-receipt-intro">
        {unavailable
          ? "For your privacy, receipt details are available only in the browser session that completed checkout."
          : verificationUncertain
            ? "We’re sorry—we could not securely confirm the final payment status. If your account was debited, please do not place another order yet."
            : "We’re sorry, but your payment could not be completed and no paid order has been confirmed. You can safely return home and try again."}
      </p>

      <section className="purchase-receipt-note is-failure" aria-labelledby="failed-help-title">
        <ReceiptText aria-hidden="true" />
        <div>
          <h2 id="failed-help-title">What to do next</h2>
          <p>
            {verificationUncertain
              ? "Keep the reference below and contact support so the order can be checked before you retry. If no amount was debited, you may return home and begin checkout again."
              : "If your account was debited, do not retry immediately. The amount is normally reversed by your bank; keep the reference below if you need to contact support. Otherwise, return home and begin checkout again whenever you are ready."}
          </p>
        </div>
      </section>

      <p className="purchase-receipt-apology">
        We hope your next passage into the world of the Pehuans is a smoother
        one.
      </p>

      <div className="purchase-receipt-signoff">
        <span>Warmly,</span>
        <strong>Mahesh Thakur</strong>
        <small>Author</small>
      </div>

      <ReturnHomeLink />

      {reference && (
        <p className="purchase-receipt-reference">Reference: {reference}</p>
      )}
    </>
  );
}

export default function PurchaseReceiptPage({ previewOutcome }) {
  const location = useLocation();
  const isDevelopmentPreview =
    process.env.NODE_ENV === "development" && Boolean(previewOutcome);
  const outcome = getOutcome(
    location.state,
    isDevelopmentPreview ? previewOutcome : null,
  );
  const isSuccess = outcome?.status === "success";

  return (
    <main className="purchase-receipt-page" aria-labelledby="purchase-receipt-title">
      <OathboundFrame />

      <section className="purchase-receipt-content">
        {isDevelopmentPreview && (
          <p className="purchase-receipt-preview-badge">Development preview</p>
        )}
        <article
          className={`purchase-receipt-card ${
            isSuccess ? "is-success" : "is-failure"
          }`}
        >
          <div className="purchase-receipt-body">
            <img
              className="purchase-receipt-logo"
              src="/media/images/Oath_logo.avif"
              alt="The Oath of the Unhuan"
            />

            {isSuccess ? (
              <SuccessReceipt receipt={outcome.receipt} />
            ) : (
              <FailureReceipt
                failure={outcome?.failure}
                unavailable={!outcome}
              />
            )}
          </div>

          <div className="purchase-receipt-branding" aria-hidden="true">
            <img
              src="/media/images/Receipt_branding.avif"
              alt=""
            />
          </div>
        </article>

        <p className="purchase-receipt-footer">
          <Link to="/#contact">Contact us</Link>
        </p>
      </section>
    </main>
  );
}
